import React, { Dispatch, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import useAppContext from "../../context/hooks/useAppContext";
import useFleetManagerContext from "../../context/hooks/useFleetManagerContext";
import { getRegistrationIdByParkingRightId } from "../../helpers/parkingRight.utils";
import { RevokeParkingRightsModel } from "../../models/RevokeParkingRightsModel";
import { useAppSelector } from "../../store";
import parkingProductsActions from "../../store/actions/parkingProducts.actions";
import { RootReducer } from "../../store/reducers";
import Warning from "./Warning";
import { selectDefaultRevocationEmailValue } from "../../containers/FleetManager/MyProducts/ParkingRightsDataTable/common";

interface IFormProps extends StateProps, DispatchProps {
    selectedParkingRightIds: string[];
    onRefresh: () => void;
}

const BatchRevokeParkingRightsWarning = ({
    parkingProducts,
    revokeParkingRights,
    selectedParkingRightIds,
    onRefresh
}: IFormProps) => {
    const { t } = useTranslation(["revokeDelegeeWarning", "globals"]);
    const { toggleBatchRevokeParkingRightsWarning, setSelectedParkingRightIds } = useFleetManagerContext();
    const { appState } = useAppContext();
    const delegatedParkingProducts = useAppSelector((state) => state.parkingProducts.delegatedParkingProducts);
    const delegees = useAppSelector((state) => state.parkingProducts.delegees);
    const defaultEmailValue = useAppSelector(selectDefaultRevocationEmailValue);

    const [sendRevocationConfirmationEmail, setSendRevocationConfirmationEmail] = useState(defaultEmailValue);

    const parkingRightsCount = selectedParkingRightIds.length;
    const warningText = t("warning", {count:parkingRightsCount});
    const warningDetails = t("details", {count:parkingRightsCount});

    const handleRevokeCancellation = () => {
        toggleBatchRevokeParkingRightsWarning();
    }

    const handleSuccessfulRevoke = () => {
        setSelectedParkingRightIds([]);
        toggleBatchRevokeParkingRightsWarning();

        onRefresh();
    }

    const handleRevokeConfirmation = () => {
        const parkingRights = selectedParkingRightIds.map(id => getRegistrationIdByParkingRightId(id, delegatedParkingProducts, delegees));
        const model: RevokeParkingRightsModel = {
            parkingRights,
            seasonTicketOwnerCrmId: appState.user.seasonTicketOwnerCrmId!,
            sendRevocationConfirmationEmail,
        };
        revokeParkingRights(model, handleSuccessfulRevoke);
    }

    return (
        <Warning
            warning={warningText}
            details={warningDetails}
            onCancel={handleRevokeCancellation}
            onConfirm={handleRevokeConfirmation}
            disabled={false}
            confirmText={t("globals:revoke")}
            loading={parkingProducts.loading}
            children={
                <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                    <qp-switch
                        data-size="s"
                        data-checked={sendRevocationConfirmationEmail}
                        onClick={() => setSendRevocationConfirmationEmail(!sendRevocationConfirmationEmail)}
                    />
                    <span>{t("revokeDelegeeWarning:sendRevocationEmail")}</span>
                </div>
            }
        >
        </Warning>
    );
};

const mapStateToProps = (state: RootReducer) => {
  const { parkingProducts } = state;

  return {
    parkingProducts,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({

    revokeParkingRights: (model: RevokeParkingRightsModel, callback?: () => void) => {
        dispatch(parkingProductsActions.revokeParkingRights(model, callback))
    },
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export default connect(mapStateToProps, mapDispatchToProps)(BatchRevokeParkingRightsWarning);
